/**
 * phiSanitizer
 * Utilidad para desensibilizar Protected Health Information (PHI) antes de
 * enviar datos a servicios externos como OpenAI.
 *
 * HIPAA Requirement: 45 CFR 164.514 - De-identification of PHI
 *
 * ============================================================================
 * DOCUMENTACIÓN PARA REFACTORIZACIÓN CON BACKEND
 * ============================================================================
 *
 * ESTADO ACTUAL (Frontend):
 * - Sanitiza datos en el frontend antes de enviar a OpenAI
 * - Reemplaza identificadores con tokens genéricos
 * - Mantiene la utilidad de los datos para análisis clínico
 *
 * CAMBIOS REQUERIDOS EN BACKEND:
 * 1. Crear proxy endpoint POST /api/ai/analyze que:
 *    - Reciba datos del frontend
 *    - Aplique sanitización adicional en el servidor
 *    - Envíe a OpenAI
 *    - Retorne respuesta al frontend
 *
 * 2. Implementar sanitización más robusta con NLP para detectar PHI en texto libre
 *
 * 3. Considerar usar OpenAI con BAA (Business Associate Agreement)
 *
 * REFACTORIZACIÓN FUTURA:
 * 1. Mover sanitización al backend
 * 2. Implementar tokenización reversible (para re-identificar si es necesario)
 * 3. Usar APIs de de-identificación especializadas (AWS Comprehend Medical, etc.)
 *
 * ============================================================================
 *
 * SAFE HARBOR METHOD (18 Identifiers to Remove):
 * 1. Names
 * 2. Geographic data (address, city, zip)
 * 3. Dates (except year) related to individual
 * 4. Phone numbers
 * 5. Fax numbers
 * 6. Email addresses
 * 7. Social Security numbers
 * 8. Medical record numbers
 * 9. Health plan beneficiary numbers
 * 10. Account numbers
 * 11. Certificate/license numbers
 * 12. Vehicle identifiers
 * 13. Device identifiers
 * 14. Web URLs
 * 15. IP addresses
 * 16. Biometric identifiers
 * 17. Full-face photos
 * 18. Any other unique identifier
 *
 * ============================================================================
 */

/**
 * Patrones regex para detectar PHI
 */
const PHI_PATTERNS = {
  // SSN: 123-45-6789 or 123456789
  ssn: /\b\d{3}[-]?\d{2}[-]?\d{4}\b/g,

  // Phone: various formats
  phone: /\b(\+?1[-.]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g,

  // Email
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,

  // Date of birth patterns: MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD
  dateOfBirth: /\b(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-](19|20)\d{2}\b|\b(19|20)\d{2}[\/\-](0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12][0-9]|3[01])\b/g,

  // ZIP codes
  zipCode: /\b\d{5}(-\d{4})?\b/g,

  // Medical Record Number (common patterns)
  mrn: /\b(MRN|MR#?|Medical Record|Record #?:?)\s*[A-Z0-9]{5,15}\b/gi,

  // Member ID patterns
  memberId: /\b(Member\s*ID|MemberID|Member#|ID#?:?)\s*[A-Z0-9]{5,20}\b/gi,

  // IP addresses
  ipAddress: /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g,

  // Credit card numbers
  creditCard: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,

  // Street addresses (basic pattern)
  streetAddress: /\b\d{1,5}\s+[\w\s]{1,30}\s+(street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|way|court|ct|circle|cir|place|pl)\b/gi,
};

/**
 * Palabras clave que indican nombres (para detección contextual)
 */
const NAME_INDICATORS = [
  "patient",
  "name",
  "mr.",
  "mrs.",
  "ms.",
  "dr.",
  "member",
];

/**
 * Generar placeholder para datos sanitizados
 */
const generatePlaceholder = (type, index = "") => {
  const placeholders = {
    name: `[PATIENT${index}]`,
    ssn: "[SSN-REDACTED]",
    phone: "[PHONE-REDACTED]",
    email: "[EMAIL-REDACTED]",
    dob: "[DOB-REDACTED]",
    date: "[DATE-REDACTED]",
    address: "[ADDRESS-REDACTED]",
    mrn: "[MRN-REDACTED]",
    memberId: "[MEMBER-ID-REDACTED]",
    zipCode: "[ZIP-REDACTED]",
    ip: "[IP-REDACTED]",
    creditCard: "[CC-REDACTED]",
    generic: "[PHI-REDACTED]",
  };
  return placeholders[type] || placeholders.generic;
};

/**
 * Sanitizar texto libre removiendo PHI detectado por patrones
 */
const sanitizeText = (text) => {
  if (!text || typeof text !== "string") return text;

  let sanitized = text;

  // Aplicar todos los patrones de PHI
  sanitized = sanitized.replace(PHI_PATTERNS.ssn, generatePlaceholder("ssn"));
  sanitized = sanitized.replace(PHI_PATTERNS.phone, generatePlaceholder("phone"));
  sanitized = sanitized.replace(PHI_PATTERNS.email, generatePlaceholder("email"));
  sanitized = sanitized.replace(PHI_PATTERNS.dateOfBirth, generatePlaceholder("dob"));
  sanitized = sanitized.replace(PHI_PATTERNS.mrn, generatePlaceholder("mrn"));
  sanitized = sanitized.replace(PHI_PATTERNS.memberId, generatePlaceholder("memberId"));
  sanitized = sanitized.replace(PHI_PATTERNS.ipAddress, generatePlaceholder("ip"));
  sanitized = sanitized.replace(PHI_PATTERNS.creditCard, generatePlaceholder("creditCard"));
  sanitized = sanitized.replace(PHI_PATTERNS.streetAddress, generatePlaceholder("address"));
  sanitized = sanitized.replace(PHI_PATTERNS.zipCode, generatePlaceholder("zipCode"));

  return sanitized;
};

/**
 * Calcular edad a partir de fecha de nacimiento
 */
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
};

/**
 * Sanitizar objeto de paciente para envío a OpenAI
 */
const sanitizePatient = (patient) => {
  if (!patient) return null;

  return {
    // Usar edad en lugar de fecha de nacimiento exacta
    age: calculateAge(patient.dateOfBirth || patient.dob),

    // Mantener género (no es identificador único)
    gender: patient.gender,

    // NO incluir:
    // - name, memberFullName, firstName, lastName
    // - dateOfBirth, dob
    // - memberId, patientId, mrn
    // - address, phone, email
    // - ssn
  };
};

/**
 * Sanitizar nota clínica para envío a OpenAI
 */
const sanitizeClinicalNote = (note) => {
  if (!note) return null;

  // Si es string, sanitizar directamente
  if (typeof note === "string") {
    return sanitizeText(note);
  }

  // Si es objeto, sanitizar campos relevantes
  return {
    // Mantener fecha solo como año o remover
    noteDate: note.noteDate ? new Date(note.noteDate).getFullYear() : null,

    // Sanitizar contenido de texto
    content: sanitizeText(note.content || note.noteText || note.text),

    // Mantener tipo de nota (no es PHI)
    noteType: note.noteType || note.type,

    // NO incluir:
    // - patientId, memberId
    // - providerName (podría ser PHI indirecto)
    // - específicos de identificación
  };
};

/**
 * Sanitizar datos de diagnóstico/condiciones
 */
const sanitizeDiagnosis = (diagnosis) => {
  if (!diagnosis) return null;

  // Si es array, sanitizar cada elemento
  if (Array.isArray(diagnosis)) {
    return diagnosis.map(sanitizeDiagnosis);
  }

  return {
    // Códigos ICD son seguros
    icdCode: diagnosis.icdCode || diagnosis.code,
    description: diagnosis.description || diagnosis.name,

    // Mantener fechas solo como año
    diagnosisYear: diagnosis.diagnosisDate
      ? new Date(diagnosis.diagnosisDate).getFullYear()
      : null,

    // NO incluir fechas exactas ni IDs
  };
};

/**
 * Sanitizar datos de medicamentos
 */
const sanitizeMedications = (medications) => {
  if (!medications) return null;

  if (Array.isArray(medications)) {
    return medications.map((med) => ({
      // Nombre del medicamento es seguro
      name: med.name || med.medicationName,
      dosage: med.dosage,
      frequency: med.frequency,

      // NO incluir fechas exactas de prescripción ni prescriptor
    }));
  }

  return medications;
};

/**
 * Sanitizar datos de laboratorio
 */
const sanitizeLabResults = (labs) => {
  if (!labs) return null;

  if (Array.isArray(labs)) {
    return labs.map((lab) => ({
      testName: lab.testName || lab.name,
      value: lab.value || lab.result,
      unit: lab.unit,
      referenceRange: lab.referenceRange,
      isAbnormal: lab.isAbnormal || lab.abnormal,

      // Mantener solo año del resultado
      resultYear: lab.resultDate ? new Date(lab.resultDate).getFullYear() : null,
    }));
  }

  return labs;
};

/**
 * API pública del sanitizador de PHI
 */
const phiSanitizer = {
  /**
   * Sanitizar texto libre
   */
  sanitizeText,

  /**
   * Sanitizar objeto de paciente
   */
  sanitizePatient,

  /**
   * Sanitizar nota clínica
   */
  sanitizeClinicalNote,

  /**
   * Sanitizar diagnósticos
   */
  sanitizeDiagnosis,

  /**
   * Sanitizar medicamentos
   */
  sanitizeMedications,

  /**
   * Sanitizar resultados de laboratorio
   */
  sanitizeLabResults,

  /**
   * Sanitizar objeto completo para envío a IA
   * Acepta cualquier estructura y sanitiza recursivamente
   */
  sanitizeForAI: (data) => {
    if (!data) return null;

    // Si es string, sanitizar texto
    if (typeof data === "string") {
      return sanitizeText(data);
    }

    // Si es array, sanitizar cada elemento
    if (Array.isArray(data)) {
      return data.map((item) => phiSanitizer.sanitizeForAI(item));
    }

    // Si es objeto, sanitizar propiedades conocidas
    if (typeof data === "object") {
      const sanitized = {};

      for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();

        // Campos a OMITIR completamente
        if (
          lowerKey.includes("name") ||
          lowerKey.includes("fullname") ||
          lowerKey.includes("firstname") ||
          lowerKey.includes("lastname") ||
          lowerKey.includes("ssn") ||
          lowerKey.includes("socialsecurity") ||
          lowerKey.includes("address") ||
          lowerKey.includes("street") ||
          lowerKey.includes("city") ||
          lowerKey.includes("state") ||
          lowerKey.includes("zip") ||
          lowerKey.includes("phone") ||
          lowerKey.includes("email") ||
          lowerKey.includes("memberid") ||
          lowerKey.includes("patientid") ||
          lowerKey.includes("mrn")
        ) {
          // Omitir estos campos
          continue;
        }

        // Campos de fecha de nacimiento -> convertir a edad
        if (
          lowerKey.includes("dob") ||
          lowerKey.includes("dateofbirth") ||
          lowerKey.includes("birthdate")
        ) {
          sanitized.age = calculateAge(value);
          continue;
        }

        // Campos de texto libre -> sanitizar
        if (
          lowerKey.includes("note") ||
          lowerKey.includes("content") ||
          lowerKey.includes("text") ||
          lowerKey.includes("comment") ||
          lowerKey.includes("description")
        ) {
          sanitized[key] =
            typeof value === "string" ? sanitizeText(value) : value;
          continue;
        }

        // Otros campos -> sanitizar recursivamente si es objeto/array
        if (typeof value === "object" && value !== null) {
          sanitized[key] = phiSanitizer.sanitizeForAI(value);
        } else if (typeof value === "string") {
          sanitized[key] = sanitizeText(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    }

    return data;
  },

  /**
   * Verificar si un texto contiene posible PHI
   */
  containsPHI: (text) => {
    if (!text || typeof text !== "string") return false;

    for (const pattern of Object.values(PHI_PATTERNS)) {
      if (pattern.test(text)) {
        pattern.lastIndex = 0; // Reset regex
        return true;
      }
    }

    return false;
  },

  /**
   * Obtener lista de PHI detectado en texto (para debugging)
   */
  detectPHI: (text) => {
    if (!text || typeof text !== "string") return [];

    const detected = [];

    for (const [type, pattern] of Object.entries(PHI_PATTERNS)) {
      const matches = text.match(pattern);
      if (matches) {
        detected.push({
          type,
          count: matches.length,
          // No incluir los matches reales por seguridad
        });
      }
      pattern.lastIndex = 0;
    }

    return detected;
  },
};

export default phiSanitizer;
